/**
 * Device Code Flow authentication
 *
 * Cross-platform authentication using device code flow.
 * Properly persists refresh tokens and works reliably across process restarts.
 * Works on Windows, macOS, and Linux.
 */

// DECISION (2026-07-20, maintainer): device code is THE interactive sign-in
// flow, on every platform. Alternatives were evaluated and declined:
//   - WAM (Windows native broker): Windows-only, broker tokens don't persist
//     across processes (see wam-broker.ts), unreliable elsewhere.
//   - Loopback browser auth (localhost redirect): requires a browser on the
//     SAME machine — fails over SSH, in containers, and on headless Linux.
// Device code works identically on Windows/macOS/Linux and lets the user
// complete sign-in from ANY second device (including a phone). Uniformity
// beats a marginally slicker flow that only works sometimes. Accepted
// tradeoff: tenants whose Conditional Access blocks device-code grants have
// no in-product fallback (the sign-in error guidance names the fix).
// Do not add alternative interactive flows without revisiting this decision.

import {
  PublicClientApplication,
  LogLevel,
  type Configuration,
  type AccountInfo,
  type AuthenticationResult,
  type DeviceCodeRequest,
} from "@azure/msal-node";
import type { AuthProvider, WamAuthConfig } from "./types.js";
import { RESOURCES } from "./types.js";
import { AuthenticationError, silentOnlyAuthError, isInteractionRequiredError } from "../utils/errors.js";
import { DEFAULT_AUTHORITY, SCOPES, isAllowedTokenDomain } from "../utils/constants.js";
import { validateTenantId, validateHttpsUrl, tenantAuthorityFor, GUID_PATTERN } from "../utils/security.js";
import { createPersistentCachePlugin, permitEmptyTokenCacheWrite } from "./token-cache.js";
import { saveAccount, loadAccount, clearAccountCache } from "./account-cache.js";
import { probeDeviceCodeEndpoint } from "./device-code-probe.js";
import { logger } from "../utils/logger.js";

/**
 * Create a device code flow authentication provider
 * @param silentOnly - If true, only attempt silent auth (for MCP server mode)
 */
export async function createDeviceCodeAuthProvider(
  config: WamAuthConfig,
  silentOnly = false
): Promise<AuthProvider> {
  const { clientId, tenantId, cachePath } = config;

  // Build authority URL - use "organizations" for multi-tenant work accounts
  // Validate tenant ID to prevent authority URL manipulation
  const validatedTenantId = tenantId ? validateTenantId(tenantId) : null;
  const authority = validatedTenantId
    ? `https://login.microsoftonline.com/${validatedTenantId}`
    : DEFAULT_AUTHORITY;

  // Create persistent cache plugin for token storage
  const cachePlugin = await createPersistentCachePlugin(cachePath);
  // Non-PII SDK messages can still contain claims or tokens. Keep suppression
  // active until all overlapping selected-account silent acquisitions settle.
  let sensitiveAcquisitionsInFlight = 0;

  // MSAL configuration with persistent cache (no WAM broker)
  const msalConfig: Configuration = {
    auth: {
      clientId,
      authority,
    },
    cache: {
      cachePlugin,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          if (containsPii || sensitiveAcquisitionsInFlight > 0) return;
          // Only log Error, Warning, Info from MSAL — suppress Verbose/Trace
          // to prevent leaking internal MSAL details even when LOG_LEVEL=trace
          switch (level) {
            case LogLevel.Error:
              logger.error({ source: "msal" }, message);
              break;
            case LogLevel.Warning:
              logger.warn({ source: "msal" }, message);
              break;
            case LogLevel.Info:
              logger.debug({ source: "msal" }, message);
              break;
            default:
              // Verbose/Trace intentionally suppressed
              break;
          }
        },
        piiLoggingEnabled: false,
      },
    },
  };

  const pca = new PublicClientApplication(msalConfig);
  let currentAccount: AccountInfo | null = null;
  let interactiveAuthCompleted = false; // tracks if device code succeeded this session
  const pendingTokenRequests = new Map<string, Promise<AuthenticationResult>>();

  // Try to get cached account
  currentAccount = loadAccount();

  if (!currentAccount) {
    try {
      const accounts = await pca.getAllAccounts();
      if (accounts.length > 0) {
        currentAccount = accounts[0] ?? null;
      }
    } catch (err) {
      logger.debug({ err: err instanceof Error ? err.message : String(err) }, "Failed to load cached accounts");
    }
  }

  /**
   * Acquire token - tries silent first, then device code if allowed.
   * Uses a mutex to prevent concurrent token refresh races.
   */
  async function acquireToken(
    scopes: readonly string[]
  ): Promise<AuthenticationResult> {
    // Key mutex per sorted scope set to prevent wrong-token returns
    const key = [...scopes].sort().join("|");
    const pending = pendingTokenRequests.get(key);
    if (pending) {
      return pending;
    }
    const promise = acquireTokenInternal(scopes);
    pendingTokenRequests.set(key, promise);
    try {
      return await promise;
    } finally {
      pendingTokenRequests.delete(key);
    }
  }

  async function acquireTokenInternal(
    scopes: readonly string[]
  ): Promise<AuthenticationResult> {
    const mutableScopes = [...scopes];
    let silentError: unknown;

    // The account may have appeared AFTER this provider was created — the
    // in-chat sign_in tool and `--login` both write to the shared cache while
    // the server is running. Re-check before failing so a fresh sign-in is
    // picked up without a restart.
    if (!currentAccount) {
      currentAccount = loadAccount();
      if (!currentAccount) {
        try {
          const accounts = await pca.getAllAccounts();
          currentAccount = accounts[0] ?? null;
        } catch {
          // cache unreadable — proceed to the normal failure paths
        }
      }
      if (currentAccount) {
        logger.info({ username: currentAccount.username }, "Picked up newly signed-in account");
      }
    }

    // Try silent acquisition first
    if (currentAccount) {
      try {
        logger.debug({ username: currentAccount.username }, "Attempting silent token acquisition");
        // Pin the authority to the account's home tenant: silent redemption
        // against /common fails for some accounts even though the same
        // refresh token redeems fine tenant-specifically. MSAL honors a
        // per-request authority even when the PCA was built with /common.
        const result = await pca.acquireTokenSilent({
          scopes: mutableScopes,
          account: currentAccount,
          authority: tenantAuthorityFor(currentAccount.tenantId, authority),
        });
        logger.debug("Silent token acquisition succeeded");
        return result;
      } catch (err) {
        logger.debug({ err }, "Silent token acquisition failed");
        silentError = err;
        // Silent failed, will try device code if allowed
      }
    }

    // In silent-only mode (MCP server), fail fast — surfacing consent errors
    // (AADSTS65001) distinctly so users aren't told to re-run --setup for a
    // problem that --setup can't fix.
    if (silentOnly) {
      throw silentOnlyAuthError(silentError, mutableScopes);
    }

    // If we already completed interactive auth this session, subsequent
    // failures are secondary resource probes (e.g., BAP API). When Microsoft
    // explicitly demands interaction (consent/MFA/policy), only an
    // interactive flow can satisfy it — fall through to device code (we are
    // in an interactive context; the server path bailed at silentOnly above).
    // Anything else fails with the REAL error: the old hardcoded "resource
    // may not be registered" message sent users chasing app-registration
    // problems that did not exist.
    if (currentAccount && interactiveAuthCompleted) {
      if (!isInteractionRequiredError(silentError)) {
        const detail =
          silentError instanceof Error ? silentError.message : String(silentError ?? "unknown error");
        throw new AuthenticationError(
          `Silent token acquisition failed for scopes ${mutableScopes.join(", ")}: ${detail}`
        );
      }
      logger.info(
        { scopes: mutableScopes },
        "Silent acquisition requires interaction — starting device code for this resource"
      );
    }

    // Device code flow for interactive authentication (initial sign-in only)
    //
    // Failure modes seen in the wild:
    //   * `response.message` missing — Microsoft's STS sometimes omits the
    //     pre-formatted prompt (non-English locales, strict B2B). Fixed in
    //     0.7.7 by synthesizing the prompt from userCode + verificationUri.
    //   * `response.userCode` missing too — tenant blocks device-code flow,
    //     app registration lacks public-client capability, or a corporate
    //     proxy strips OAuth fields. MSAL invokes the callback with an
    //     empty/partial response, then polling fails immediately with
    //     `invalid_grant`. We detect this here and replace the cryptic MSAL
    //     error with an actionable diagnostic so users know what to fix.
    let emptyUserCodeDetected = false;

    try {
      logger.info("Starting device code authentication flow");

      const deviceCodeRequest: DeviceCodeRequest = {
        scopes: mutableScopes,
        deviceCodeCallback: (response) => {
          const url = response.verificationUri || "https://microsoft.com/devicelogin";
          const code = response.userCode || "(code missing)";

          // Always log the response shape (no PII) so we can diagnose tenant
          // anomalies without needing the user to re-run with debug logging.
          logger.info(
            {
              hasMessage: !!response.message,
              hasUserCode: !!response.userCode,
              hasVerificationUri: !!response.verificationUri,
              expiresIn: response.expiresIn,
              interval: response.interval,
            },
            "Device-code response received"
          );

          if (!response.userCode) {
            emptyUserCodeDetected = true;
            logger.error(
              "Device-code response is missing userCode — tenant likely blocks device-code flow"
            );
          } else if (!response.message) {
            logger.warn(
              { userCode: code, verificationUri: url },
              "Device-code response missing 'message' field — using synthesized fallback"
            );
          }

          const display =
            response.message ||
            `To sign in, open ${url} in a browser on any device and enter the code ${code} to authenticate.`;
          console.log("");
          console.log(`  \x1b[36m?\x1b[0m ${display}`);
          console.log("");
          console.log(`  \x1b[33m⚠\x1b[0m  SECURITY: Do not share this code. It expires in ${Math.floor((response.expiresIn ?? 900) / 60)} minutes.`);
          console.log("");
        },
      };

      const result = await pca.acquireTokenByDeviceCode(deviceCodeRequest);

      if (!result) {
        throw new AuthenticationError("Device code authentication returned null");
      }

      if (result.account) {
        currentAccount = result.account;
        saveAccount(result.account);
      }

      interactiveAuthCompleted = true;
      logger.info({ username: result.account?.username }, "Device code authentication succeeded");
      return result;
    } catch (err) {
      if (err instanceof AuthenticationError) {
        throw err;
      }
      // If we got an empty userCode in the callback, the cryptic MSAL error
      // ("post_request_failed: ... invalid_grant") is a downstream symptom.
      // Re-probe the endpoint directly to surface the real AAD error.
      if (emptyUserCodeDetected) {
        const probe = await probeDeviceCodeEndpoint(clientId, validatedTenantId);
        if (probe?.appNotFound) {
          throw new AuthenticationError(
            `The configured app registration (${clientId}) no longer exists in this tenant (AADSTS700016) — ` +
            "it may have been deleted by an administrator. " +
            "Run 'powerautomate-mcp --setup' to register a new app or enter a different Client ID.",
            err
          );
        }
        const tenantDetail = probe?.errorDescription
          ? ` Tenant response: ${probe.errorDescription.slice(0, 300)}`
          : "";
        throw new AuthenticationError(
          "Device-code flow returned no usable code from your tenant. " +
          "This typically means one of: " +
          "(1) your tenant blocks device-code flow via a Conditional Access policy — ask your tenant admin to allow device-code grants for this app, " +
          "(2) the app registration is missing 'Allow public client flows' — set it under Authentication > Advanced settings in the Azure Portal and re-run --setup, or " +
          "(3) a corporate proxy is stripping OAuth fields from the response — try a direct internet connection to confirm. " +
          `Underlying MSAL error: ${err instanceof Error ? err.message : String(err)}.${tenantDetail}`,
          err
        );
      }
      throw new AuthenticationError(
        `Failed to authenticate: ${err instanceof Error ? err.message : String(err)}`,
        err
      );
    }
  }

  const provider: AuthProvider = {
    async getPowerPlatformTokenSilentForCurrentAccount(expectedTenantId: string): Promise<string> {
      const unavailable = () =>
        new AuthenticationError(
          "Selected-account Power Platform authentication unavailable; no fallback or sign-in was attempted.",
        );
      const account = currentAccount;
      if (
        !account ||
        typeof expectedTenantId !== "string" ||
        expectedTenantId.length !== 36 ||
        !GUID_PATTERN.test(expectedTenantId) ||
        typeof account.tenantId !== "string" ||
        account.tenantId.length !== 36 ||
        !GUID_PATTERN.test(account.tenantId) ||
        account.tenantId.toLowerCase() !== expectedTenantId.toLowerCase()
      ) {
        throw unavailable();
      }
      const snapshot = { ...account };
      const identityKeys = [
        "homeAccountId",
        "localAccountId",
        "tenantId",
        "environment",
        "username",
      ] as const;
      if (
        identityKeys.some((key) => typeof snapshot[key] !== "string" || !snapshot[key].trim()) ||
        snapshot.localAccountId.length !== 36 ||
        !GUID_PATTERN.test(snapshot.localAccountId)
      ) {
        throw unavailable();
      }
      sensitiveAcquisitionsInFlight++;
      try {
        // Fixed audience and tenant, no claims, forceRefresh or interactive fallback.
        const result = await pca.acquireTokenSilent({
          account,
          scopes: [`${RESOURCES.POWER_PLATFORM_API}/.default`],
          authority: tenantAuthorityFor(snapshot.tenantId, authority),
        });
        if (
          currentAccount !== account ||
          identityKeys.some(
            (key) => result.account?.[key] !== snapshot[key] || currentAccount?.[key] !== snapshot[key],
          ) ||
          typeof result.accessToken !== "string" ||
          !result.accessToken.trim() ||
          /\s/.test(result.accessToken)
        ) {
          throw unavailable();
        }
        return result.accessToken;
      } catch {
        // Do not attach SDK errors, challenges, claims, account values or tokens.
        throw unavailable();
      } finally {
        sensitiveAcquisitionsInFlight--;
      }
    },

    async getToken(): Promise<string> {
      const result = await acquireToken(SCOPES.FLOW);
      return result.accessToken;
    },

    async getTokenForResource(resource: string): Promise<string> {
      validateHttpsUrl(resource, "Token resource");
      const url = new URL(resource);
      if (!isAllowedTokenDomain(url.hostname)) {
        throw new AuthenticationError(`Token resource not in allowlist: ${url.hostname}`);
      }
      const scopes = [`${resource}/.default`] as const;
      const result = await acquireToken(scopes);
      return result.accessToken;
    },

    async tryGetTokenForResourceSilent(resource: string): Promise<string | null> {
      try {
        validateHttpsUrl(resource, "Token resource");
        const url = new URL(resource);
        if (!isAllowedTokenDomain(url.hostname)) {
          return null;
        }
        if (!currentAccount) {
          return null;
        }
        const scopes = [`${resource}/.default`];
        const result = await pca.acquireTokenSilent({
          scopes,
          account: currentAccount,
          authority: tenantAuthorityFor(currentAccount.tenantId, authority),
        });
        return result.accessToken;
      } catch {
        return null;
      }
    },

    async logout(): Promise<void> {
      if (currentAccount) {
        const cache = pca.getTokenCache();
        // A sign-out legitimately persists an account-less cache — permit it
        // past the empty-write guard.
        permitEmptyTokenCacheWrite();
        await cache.removeAccount(currentAccount);
        currentAccount = null;
      }
      clearAccountCache();
    },

    getCurrentAccount(): AccountInfo | null {
      return currentAccount;
    },

    async hasAccount(): Promise<boolean> {
      const allAccounts = await pca.getAllAccounts();
      return allAccounts.length > 0;
    },
  };

  return provider;
}

/**
 * Create auth provider from config using device code flow
 * @param silentOnly - If true, only attempt silent auth (for MCP server mode)
 */
export async function createAuthProviderFromConfig(
  clientId: string,
  tenantId?: string | null,
  cachePath?: string,
  silentOnly = false
): Promise<AuthProvider> {
  const config: WamAuthConfig = {
    clientId,
    tenantId: tenantId ?? null,
  };
  if (cachePath !== undefined) {
    config.cachePath = cachePath;
  }
  return createDeviceCodeAuthProvider(config, silentOnly);
}
